import * as LP from '../languageProcessing/index';
import * as E from '../errors';
import * as O from './misc';

export class BlockExprToken extends LP.BlockToken {
	override name = 'block-expression';
};

export class ObjectExprToken extends LP.BlockToken {
	override name = 'object-expression';
};

export class ParametersExprToken extends LP.BlockToken {
	override name = 'parameters-expression';
};

export class ArrayExprToken extends LP.BlockToken {
	override name = 'array-expression';
};

export class StringToken extends LP.Token<string> {
	override name = 'string';
};

export class UnOpNegationToken extends LP.UnaryToken {
	override name = 'unop-negation';
};

export class BinOpStrictEqualToken extends LP.BinaryToken {
	override name = 'binop-strict-equality';
};

export class BinOpAccessToken extends LP.BinaryToken {
	override name = 'binop-access';
};

export class BinOpCallToken extends LP.BinaryToken {
	override name = 'binop-call';
};

export class BinOpAddToken extends LP.BinaryToken {
	override name = 'binop-add';
};

export class BinOpDivideToken extends LP.BinaryToken {
	override name = 'binop-divide';
};

export class ObjectPropertyToken extends LP.Token<{
	key: string;
	value: LP.Token<any>;
}> {
	name = 'objectProperty';

	override getTokensList(): LP.TokenList[] {
		return ([
			LP.PersistentTokenListSealed.create([this.value.value])
		]);
	};
};
export type TPropertyPath = StaticPropertyPath | DynamicPropertyPath;

export abstract class PropertyPath {
	private static cache: Map<
		string,
		TPropertyPath
	> = new Map;

	static create(
		sourcePath: string
	): TPropertyPath {
		const cpp = this.cache.get(sourcePath);
		if (cpp) {
			cpp.reference++;
			return (cpp);
		}
		return (
			sourcePath.startsWith('[*]') ? new DynamicPropertyPath(sourcePath) :
				new StaticPropertyPath(sourcePath)
		);
	};

	static evaluate(
		sourcePath: string,
		obj: Record<any, any>
	) {
		const i = this.create(sourcePath);
		const r = i.evaluate(obj);
		i.free();
		return (r);
	};

	private reference = 1;
	protected abstract compiled: unknown;
	sourcePath: string;

	/**
	 * A property path cannot be automatically garbage collected, so call free if you don't need it anymore.
	 **/
	free(delay?: number) {
		//free only after 1min delay
		//to prevent creating new instance immediatly after freeing one
		setTimeout(() => {
			this.reference--;
			if (!this.reference) {
				PropertyPath.cache.delete(this.sourcePath);
			}
		}, delay || (60 * 1000));
	};

	abstract evaluate(obj: Record<any, any>): unknown;

	// abstract evaluateSync(obj : Record<any, any>) : Promise<unknown> | unknown;
	protected constructor(sourcePath: string) {
		this.sourcePath = sourcePath;
		PropertyPath.cache.set(sourcePath, this as any as TPropertyPath);
	};
};

export class StaticPropertyPath extends PropertyPath {
	protected compiled: string[];

	evaluate(obj: Record<any, any>) {
		return (O.get(obj, this.compiled));
	};

	constructor(sourcePath: string) {
		super(sourcePath);
		this.compiled = sourcePath.split('.');
	};
};

export class DynamicPropertyPath extends PropertyPath {
	private static uniLexs: LP.IUniLex[] = [
		LP.newLineUniLex,
		LP.createUniLex('expression-block', '{'),
		LP.createUniLex('expression-block', '}'),
		LP.createUniLex('parameters-block', '('),
		LP.createUniLex('parameters-block', ')'),
		LP.createUniLex('array-block', '['),
		LP.createUniLex('array-block', ']'),
		LP.createUniLex('variable', '$'),
		LP.createUniLex('space', ' ', true),
		LP.createUniLex('space', '\t', true),
		LP.createUniLex('operator', '.'),
		LP.createUniLex('operator', '='),
		LP.createUniLex('operator', '<'),
		LP.createUniLex('operator', '>'),
		LP.createUniLex('operator', '!'),
		LP.createUniLex('operator', '|'),
		LP.createUniLex('operator', '+'),
		LP.createUniLex('operator', '/'),
		LP.createUniLex('separator', ','),
		LP.createUniLex('string-marker', '"'),
		LP.createUniLex('diez', '#')
	];

	private static async tokenEvaluate(
		token: LP.Token<any>,
		obj: Record<any, any>,
		parentToken?: LP.Token<any> // Used for contextual child token
	): Promise<any> {
		if (obj == null) {
			return (undefined);
		}
		if (token instanceof LP.LexicalToken) {
			if (token.isA('variable')) {
				return (obj);
			} else if (
				token.isA('identifier') ||
				(token.isA('number') && parentToken && (parentToken instanceof BinOpAccessToken))
			) {
				let value = obj[token.value.text];
				//Keep context in case of a method
				if (value instanceof Function) {
					value = value.bind(obj);
				}
				return (value);
			} else if (token.isA('number')) {
				return (parseFloat(token.value.text));
			}
		} else if (token instanceof StringToken) {
			return (token.value);
		} else if (token instanceof UnOpNegationToken) {
			const target = token.value.target.tokens[0] as LP.Token<any>;
			const target_value = await DynamicPropertyPath.tokenEvaluate(target, obj);
			return (!target_value);
		} else if (token instanceof BlockExprToken) {
			const entries: [string, unknown][] = (
				obj.entries ? Array.from(obj.entries()) :
					Object.entries(obj)
			);
			const tok = token.value.tokens.tokens[0] as LP.Token<any>;
			for (const [key, value] of entries) {
				const r = await DynamicPropertyPath.tokenEvaluate(tok, {
					key, value
				});
				if (r) {
					return ({
						key, value
					});
				}
			}
			return (undefined);
		} else if (token instanceof BinOpStrictEqualToken) {
			const LHS = token.value.LHS.tokens[0] as LP.Token<any>;
			const RHS = token.value.RHS.tokens[0] as LP.Token<any>;
			const lhs_value = await DynamicPropertyPath.tokenEvaluate(LHS, obj);
			const rhs_value = await DynamicPropertyPath.tokenEvaluate(RHS, obj);
			return (lhs_value === rhs_value);
		} else if (token instanceof BinOpAccessToken) {
			const LHS = token.value.LHS.tokens[0] as LP.Token<any>;
			const RHS = token.value.RHS.tokens[0] as LP.Token<any>;
			const lhs_obj = await DynamicPropertyPath.tokenEvaluate(LHS, obj);
			let value = await DynamicPropertyPath.tokenEvaluate(RHS, lhs_obj, token);
			//Keep context in case of a method
			if (value instanceof Function) {
				value = value.bind(lhs_obj);
			}
			return (value);
		} else if (token instanceof BinOpCallToken) {
			const LHS = token.value.LHS.tokens[0] as LP.Token<any>;
			const RHS = token.value.RHS.tokens[0] as ParametersExprToken;
			const method = await DynamicPropertyPath.tokenEvaluate(LHS, obj);
			if (!(method instanceof Function)) {
				throw E.user(
					'Call target is not a method!',
					{
						method,
						LHS,
						object: obj
					}
				);
			}
			const parameters = (
				await Promise.all(RHS.value.tokens.tokens.map(async x => await DynamicPropertyPath.tokenEvaluate(x, obj)))
			);
			return (await method(...parameters));
		} else if (token instanceof ArrayExprToken) {
			return (
				await Promise.all(token.value.tokens.tokens.map(async x => await DynamicPropertyPath.tokenEvaluate(x, obj)))
			);
		} else if (token instanceof BinOpAddToken) {
			return (
				await DynamicPropertyPath.tokenEvaluate(<LP.Token<any>>token.value.LHS.tokens[0], obj) +
				await DynamicPropertyPath.tokenEvaluate(<LP.Token<any>>token.value.RHS.tokens[0], obj)
			);
		} else if (token instanceof BinOpDivideToken) {
			return (
				await DynamicPropertyPath.tokenEvaluate(<LP.Token<any>>token.value.LHS.tokens[0], obj) /
				await DynamicPropertyPath.tokenEvaluate(<LP.Token<any>>token.value.RHS.tokens[0], obj)
			);
		} else if (token instanceof ObjectExprToken) {
			return Object.fromEntries(await Promise.all(token.value.tokens.tokens.map(async propertyDeclarationToken => [
				propertyDeclarationToken.value,
				await DynamicPropertyPath.tokenEvaluate(propertyDeclarationToken.value.value, obj)
			])));
		}
		throw E.user('SyntaxError', {
			token
		});
	};

	protected readonly compiled: LP.Token<any>;

	async evaluate(obj: Record<any, any>) {
		try {
			return (await DynamicPropertyPath.tokenEvaluate(this.compiled, obj));
		} catch (error: any) {
			throw E.bindAsUser(
				'Cannot evaluate!',
				{
					dynamicPropertyPath: this,
					object: obj
				},
				error
			);
		}
	};

	constructor(sourcePath: string) {
		super(sourcePath);
		//lexer
		const identifierUniLex = LP.createUniLex('identifier');
		const numberUniLex = LP.createUniLex('number');
		const opStrictEqualUniLex = LP.createUniLex('binop-strict-equality');
		const opOrUniLex = LP.createUniLex('binop-or');
		const cu = LP.lexer(
			sourcePath.slice(3),
			DynamicPropertyPath.uniLexs
		);
		const identifiers = (
			cu.tokens.getLexicalTokens().
			filter(x => x.value.unit.name === 'unknown').
			filter(x => x.value.text.match(/^[\w]+$/g)).
			filter(x => (x.value.text[0] as string).match(/^\D/g))
		);
		for (const identifier of identifiers) {
			identifier.value.unit = identifierUniLex;
		}
		const numbers = (
			cu.tokens.getLexicalTokens().
			filter(x => x.value.unit.name === 'unknown').
			filter(x => x.value.text.match(/^-?\d*\.?\d+$/g))
		);
		for (const number of numbers) {
			number.value.unit = numberUniLex;
		}
		//float numbers
		{
			const tl = cu.tokens;
			const t = tl.tokens;
			const max = tl.tokens.length;
			for (let i = 0; i < max; i++) {
				const t0 = t[i] as LP.LexicalToken;
				const t1 = t[i + 1] as LP.LexicalToken;
				const t2 = t[i + 2] as LP.LexicalToken;
				const t3 = t[i + 3] as LP.LexicalToken;
				if (
					(t0 && (t0.value.text.trim() !== '')) ||
					(!t1 || !(t1 instanceof LP.LexicalToken) || !t1.isA('number')) ||
					(!t2 || !(t2 instanceof LP.LexicalToken) || !t2.isA('operator') || (t2.value.unit.symbol !== '.')) ||
					(!t3 || !(t3 instanceof LP.LexicalToken) || !t3.isA('number'))
				) {
					continue;
				}
				const float = t1.value.text + t2.value.text + t3.value.text;
				t1.value.text = float;
				t2.remove();
				t3.remove();
				i += 2;
			}
		}
		// OR, STRICT EQUAL
		{
			const tl = cu.tokens;
			const t = tl.tokens;
			const max = tl.tokens.length;
			for (let i = 0; i < max; i++) {
				const t0 = t[i] as LP.LexicalToken;
				const t1 = t[i + 1] as LP.LexicalToken;
				const t2 = t[i + 2] as LP.LexicalToken;
				if ([t0, t1].every(x => x.is('operator', '|'))) {
					t0.transform(opOrUniLex, '||');
					t1.remove();
				} else if ([t0, t1, t2].every(x => x.is('operator', '='))) {
					t0.transform(opStrictEqualUniLex, '===');
					t1.remove();
					t2.remove();
				}
			}
		}
		//parser
		//parse string
		for (const tl of cu.getAllTokensList()) {
			tl.parseDelimited(
				(token) => {
					if (token.name === 'lex') {
						const t = token as LP.LexicalToken;
						if (t.isA('string-marker')) {
							return ('openclose');
						}
					}
					return ('unknown');
				},
				(tl, position, matches) => {
					return (new StringToken(
						matches.slice(1, -1).map(x => x.toString()).join(''),
						position.x,
						position.y,
						tl,
						true
					));
				}
			);
		}
		//parse object expr
		for (const tl of cu.getAllTokensList()) {
			tl.parseBlocks(
				ObjectExprToken,
				(token) => {
					if (token.name === 'lex') {
						const t = token as LP.LexicalToken;
						if (t.isA('expression-block')) {
							return ((t.value.text === '{') ? 'open' : 'close');
						}
					}
					return ('unknown');
				},
				(block) => {
					(block.value.tokens.tokens.slice(0)[0] as LP.Token<any>).remove();
					(block.value.tokens.tokens.slice(-1)[0] as LP.Token<any>).remove();
				}
			);
		}
		//parse block expr
		cu.visit(token => {
			if (!(token instanceof ObjectExprToken)) {
				return;
			}
			const tokens = token.value.tokens.tokens;
			const firstToken = <LP.LexicalToken>tokens.slice(0)[0];
			const lastToken = <LP.LexicalToken>tokens.slice(-1)[0];
			if (
				(tokens.length > 2) &&
				firstToken.isA('diez') &&
				lastToken.isA('diez')
			) {
				firstToken.remove();
				lastToken.remove();
				if (!token.parent) {
					throw new SyntaxError;
				}
				const block = new BlockExprToken(
					{
						tokens: new LP.TokenList(tokens.slice(1, -1))
					},
					token.position.x,
					token.position.y,
					token.parent,
					true
				);
				token.replaceBy(block);
			}
		});
		//parse parameters expr
		for (const tl of cu.getAllTokensList()) {
			tl.parseBlocks(
				ParametersExprToken,
				(token) => {
					if (token.name === 'lex') {
						const t = token as LP.LexicalToken;
						if (t.isA('parameters-block')) {
							return ((t.value.text === '(') ? 'open' : 'close');
						}
					}
					return ('unknown');
				},
				(block) => {
					(block.value.tokens.tokens.slice(0)[0] as LP.Token<any>).remove();
					(block.value.tokens.tokens.slice(-1)[0] as LP.Token<any>).remove();
				}
			);
		}
		//parse array expr
		for (const tl of cu.getAllTokensList()) {
			tl.parseBlocks(
				ArrayExprToken,
				(token) => {
					if (token.name === 'lex') {
						const t = token as LP.LexicalToken;
						if (t.isA('array-block')) {
							return ((t.value.text === '[') ? 'open' : 'close');
						}
					}
					return ('unknown');
				},
				(block) => {
					(block.value.tokens.tokens.slice(0)[0] as LP.Token<any>).remove();
					(block.value.tokens.tokens.slice(-1)[0] as LP.Token<any>).remove();
				}
			);
		}
		//Remove all lex spaces
		cu.visit(token => {
			if (
				(token instanceof LP.LexicalToken) &&
				token.isA('space')
			) {
				token.remove();
			}
		});
		//parse object property
		cu.visit(token => {
			if (!(token instanceof ObjectExprToken)) {
				return;
			}
			const getTokens = () => token.value.tokens.tokens;
			const propertiesTokens = [];
			let tokens: LP.Token<any>[];
			while (tokens = getTokens(), tokens.length !== 0) {
				if (tokens.length < 3) {
					throw new SyntaxError;
				}
				if (!(tokens[0] instanceof StringToken)) {
					throw E.user('SyntaxError: A property key must be a string!', {
						objectExpression: token,
						propertyKey: tokens[0]
					});
				}
				if (
					!(tokens[1] instanceof LP.LexicalToken) ||
					!(<LP.LexicalToken>tokens[1]).is('unknown', ':')
				) {
					throw E.user('SyntaxError: A property key value must use ":" as separator!', {
						objectExpression: token,
						propertyKey: tokens[0],
						separator: tokens[1]
					});
				}
				const t0 = tokens[0];
				const t1 = tokens[1];
				const t2 = <LP.Token<any>>tokens[2];
				t0.remove();
				t1.remove();
				t2.remove();
				const propertyToken = new ObjectPropertyToken(
					{
						key: t0.value,
						value: t2
					},
					t0.position.x,
					t0.position.y,
					token.value.tokens,
					true
				);
				propertiesTokens.push(propertyToken);
				// token.value.tokens.add(propertyToken);
				tokens = getTokens();
				if (tokens.length !== 0) {
					if (
						!(tokens[0] instanceof LP.LexicalToken) ||
						!tokens[0].isA('separator')
					) {
						throw E.user('SyntaxError: Expected property separator!', {
							objectExpression: token,
							propertyKey: t0,
							propertyValue: t1,
							separator: tokens[0]
						});
					}
					tokens[0].remove();
				}
			}
			for (const propertyToken of propertiesTokens) {
				token.value.tokens.add(propertyToken);
			}
		});
		//Check that all variables access are first child of BlockExpr
		cu.visit(token => {
			if (
				!(token instanceof LP.LexicalToken) ||
				!token.isA('variable')
			) {
				return;
			}
			if (!token.parent) {
				throw E.library('No parent!', {});
			}
			const firstChild = token.parent.tokens[0] as LP.Token<any>;
			if (firstChild !== token) {
				throw E.user('SyntaxError: A variable should be the first node in a BlockExpr!', {
					token
				});
			}
		});
		//An access must have a target host &&
		//An access must have a target property &&
		//All token must have a parent &&
		//All lhs token must be (identifier || variable || block expr || bin op) &&
		//All rhs token must be (identifier || variable || block expr || bin op)
		//So its a bin op access
		cu.visit(token => {
			if (
				(token instanceof LP.LexicalToken) &&
				token.is('operator', '.')
			) {
				const LHS = token.previous();
				const RHS = token.next();
				//An access must have a target host
				if (!LHS) {
					throw E.user('SyntaxError: ', {});
				}
				//An access must have a target property
				if (!RHS) {
					throw E.user('SyntaxError: ', {});
				}
				//All token must have a parent
				if (!token.parent) {
					throw E.library('No parent', {});
				}
				//All lhs token must be (identifier || variable || block expr || bin op)
				if (
					!(
						(LHS instanceof LP.LexicalToken) &&
						(
							LHS.isA('identifier') ||
							LHS.isA('variable')
						)
					) &&
					!(LHS instanceof BlockExprToken) &&
					!(LHS instanceof BinOpAccessToken)
				) {
					return;
				}
				//All rhs token must be (identifier || variable || block expr || bin op)
				if (
					!(
						(RHS instanceof LP.LexicalToken) &&
						RHS.isA('identifier')
					) &&
					!(RHS instanceof BlockExprToken) &&
					!(LHS instanceof BinOpAccessToken)
				) {
					return;
				}
				const marker = {} as any as LP.Token<any>;
				LHS.replaceBy(marker);
				const tok: LP.BinaryToken = new BinOpAccessToken(
					{
						LHS: LP.TokenList.from(LHS),
						RHS: LP.TokenList.from(RHS)
					},
					LHS.position.x,
					LHS.position.y,
					token.parent,
					true
				);
				token.parent.replaceBy(marker, tok);
				token.remove();
			}
		});
		//bin op call
		//todo: type LHS
		cu.visit(token => {
			if (!(token instanceof ParametersExprToken)) {
				return;
			}
			const LHS = token.previous();
			if (!LHS) {
				throw E.user('SyntaxError: Parameters expression must have a method', {
					parametersExpression: token,
					compilationUnit: cu,
					dynamicPropertyPath: this
				});
			}
			if (!token.parent || !LHS.parent) {
				throw E.library('Error', {
					LHS,
					parametersExpression: token,
					compilationUnit: cu,
					dynamicPropertyPath: this
				});
			}
			const marker = {} as any as LP.Token<any>;
			const parent = LHS.parent;
			LHS.replaceBy(marker);
			token.remove();
			const tok = new BinOpCallToken(
				{
					LHS: LP.TokenList.from(LHS),
					RHS: LP.TokenList.from(token)
				},
				LHS.position.x,
				LHS.position.y,
				parent,
				true
			);
			parent.replaceBy(marker, tok);
		});
		let hasModification = true;
		while (hasModification) {
			hasModification = false;
			//An access must have a target host &&
			//An access must have a target property &&
			//All token must have a parent &&
			//All lhs token must be bin op call &&
			//All rhs token must be (identifier || variable || block expr || bin op)
			//So its a bin op access
			cu.visit(token => {
				if (
					(token instanceof LP.LexicalToken) &&
					token.is('operator', '.')
				) {
					const LHS = token.previous();
					const RHS = token.next();
					//An access must have a target host
					if (!LHS) {
						throw E.user('SyntaxError: ', {});
					}
					//An access must have a target property
					if (!RHS) {
						throw E.user('SyntaxError: ', {});
					}
					//All token must have a parent
					if (!token.parent) {
						throw E.library('Error', {});
					}
					//All rhs token must be (identifier || variable || block expr || bin op)
					if (
						!(
							(RHS instanceof LP.LexicalToken) &&
							(
								RHS.isA('identifier') ||
								RHS.isA('number')
							)
						) &&
						!(RHS instanceof BlockExprToken) &&
						!(RHS instanceof BinOpAccessToken) &&
						!(RHS instanceof BinOpCallToken)
					) {
						return;
					}
					const marker = {} as any as LP.Token<any>;
					LHS.replaceBy(marker);
					const tok: LP.BinaryToken = new BinOpAccessToken(
						{
							LHS: LP.TokenList.from(LHS),
							RHS: LP.TokenList.from(RHS)
						},
						LHS.position.x,
						LHS.position.y,
						token.parent,
						true
					);
					token.parent.replaceBy(marker, tok);
					token.remove();
					hasModification = true;
				}
			});
		}
		//replace lex binop-strict-equality to same of sem
		cu.visit(token => {
			if (
				(token instanceof LP.LexicalToken) &&
				token.isA('binop-strict-equality')
			) {
				const LHS = token.previous();
				const RHS = token.next();
				if (!LHS) {
					throw E.user('SyntaxError: ', {});
				}
				if (!RHS) {
					throw E.user('SyntaxError: ', {});
				}
				if (!token.parent) {
					throw E.library('', {});
				}
				const tok = new BinOpStrictEqualToken(
					{
						LHS: LP.TokenList.from(LHS),
						RHS: LP.TokenList.from(RHS)
					},
					token.position.x,
					token.position.y,
					token.parent,
					true
				);
				token.replaceBy(tok);
			}
		});
		// So its a bin op /
		// todo: type lhs && rhs
		cu.visit(token => {
			if (
				(token instanceof LP.LexicalToken) &&
				token.is('operator', '/')
			) {
				const LHS = token.previous();
				const RHS = token.next();
				//An access must have a target host
				if (!LHS) {
					throw E.user('SyntaxError: ', {});
				}
				//An access must have a target property
				if (!RHS) {
					throw E.user('SyntaxError: ', {});
				}
				//All token must have a parent
				if (!token.parent) {
					throw E.library('', {});
				}
				const marker = {} as any as LP.Token<any>;
				LHS.replaceBy(marker);
				const tok = new BinOpDivideToken(
					{
						LHS: LP.TokenList.from(LHS),
						RHS: LP.TokenList.from(RHS)
					},
					LHS.position.x,
					LHS.position.y,
					token.parent,
					true
				);
				token.parent.replaceBy(marker, tok);
				token.remove();
			}
		});
		// So its a bin op +
		// todo: type lhs && rhs
		cu.visit(token => {
			if (
				(token instanceof LP.LexicalToken) &&
				token.is('operator', '+')
			) {
				const LHS = token.previous();
				const RHS = token.next();
				//An access must have a target host
				if (!LHS) {
					throw E.user('SyntaxError: ', {});
				}
				//An access must have a target property
				if (!RHS) {
					throw E.user('SyntaxError: ', {});
				}
				//All token must have a parent
				if (!token.parent) {
					throw E.library('', {});
				}
				const marker = {} as any as LP.Token<any>;
				LHS.replaceBy(marker);
				const tok = new BinOpAddToken(
					{
						LHS: LP.TokenList.from(LHS),
						RHS: LP.TokenList.from(RHS)
					},
					LHS.position.x,
					LHS.position.y,
					token.parent,
					true
				);
				token.parent.replaceBy(marker, tok);
				token.remove();
			}
		});
		// un op !
		cu.visit(token => {
			if (
				(token instanceof LP.LexicalToken) &&
				token.is('operator', '!')
			) {
				const target = token.next();
				//A negation must have a target
				if (!target) {
					throw E.user('SyntaxError: ', {});
				}
				//All token must have a parent
				if (!token.parent) {
					throw E.library('', {});
				}
				const marker = {} as any as LP.Token<any>;
				target.replaceBy(marker);
				const tok = new UnOpNegationToken(
					{
						target: LP.TokenList.from(target)
					},
					target.position.x,
					target.position.y,
					token.parent,
					true
				);
				token.parent.replaceBy(marker, tok);
				token.remove();
			}
		});
		//Remove separators in array and parameters
		cu.visit(token => {
			if (
				!(token instanceof ArrayExprToken) &&
				!(token instanceof ParametersExprToken)
			) {
				return;
			}
			const ts = token.value.tokens.tokens;
			//check errors
			for (let i = 0; i < ts.length; i++) {
				const t = <LP.Token<any>>ts[i];
				if ((i % 2) === 0) {
					if ((t instanceof LP.LexicalToken) && t.isA('separator')) {
						throw E.user(
							'SyntaxError: Expected an expression, not a separator!',
							{
								token: t,
								dynamicPropertyPath: this
							}
						);
					}
				} else {
					if (!((t instanceof LP.LexicalToken) && t.isA('separator'))) {
						throw E.user(
							'SyntaxError: Expected a separator, not an expression!',
							{
								token: t,
								dynamicPropertyPath: this
							}
						);
					}
				}
			}
			//remove separators
			token.value.tokens.filter(
				x => !((x instanceof LP.LexicalToken) && x.isA('separator'))
			);
		});
		//check errors
		// for (let tl of cu.getAllTokensList()) {
		// 	if (tl.tokens.length === 1) {
		// 		continue;
		// 	}
		// 	// throw E.library(SyntaxError, ``, {
		// 	// 	compilationUnit     : cu,
		// 	// 	dynamicPropertyPath : this
		// 	// });
		// }
		if (cu.tokens.tokens.length !== 1) {
			throw E.library(
				'SyntaxError: ',
				{
					compilationUnit: cu,
					dynamicPropertyPath: this
				}
			);
		}
		this.compiled = cu.tokens.tokens[0] as LP.Token<any>;
	};
};
